import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Suspense } from "react";
import { lazyWithRetry } from "@/lib/lazy-with-retry";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import ErrorBoundary from "@/components/ErrorBoundary";
import SafeSupportChatWidget from "@/components/SafeSupportChatWidget";

import Index from "./pages/Index.tsx";

// Lazy-load every non-landing route so the initial bundle stays small.
// Stripe, dashboard, admin, etc. are no longer in the landing bundle.
const FeedPage = lazyWithRetry(() => import("./pages/FeedPage.tsx"), "FeedPage");
const CreatorProfilePage = lazyWithRetry(() => import("./pages/CreatorProfilePage.tsx"), "CreatorProfilePage");
const OnboardingPage = lazyWithRetry(() => import("./pages/OnboardingPage.tsx"), "OnboardingPage");
const DashboardPage = lazyWithRetry(() => import("./pages/DashboardPage.tsx"), "DashboardPage");
const AuthPage = lazyWithRetry(() => import("./pages/AuthPage.tsx"), "AuthPage");
const AuthCallbackPage = lazyWithRetry(() => import("./pages/AuthCallbackPage.tsx"), "AuthCallbackPage");
const CreateRedirect = lazyWithRetry(() => import("./pages/CreateRedirect.tsx"), "CreateRedirect");
const AdminPage = lazyWithRetry(() => import("./pages/AdminPage.tsx"), "AdminPage");
const CEOOverviewPage = lazyWithRetry(() => import("./pages/CEOOverviewPage.tsx"), "CEOOverviewPage");
const TermsPage = lazyWithRetry(() => import("./pages/TermsPage.tsx"), "TermsPage");
const PrivacyPage = lazyWithRetry(() => import("./pages/PrivacyPage.tsx"), "PrivacyPage");
const CreatorAgreementPage = lazyWithRetry(() => import("./pages/CreatorAgreementPage.tsx"), "CreatorAgreementPage");
const AgeDisclaimerPage = lazyWithRetry(() => import("./pages/AgeDisclaimerPage.tsx"), "AgeDisclaimerPage");
const DmcaPage = lazyWithRetry(() => import("./pages/DmcaPage.tsx"), "DmcaPage");
const AcceptableUsePage = lazyWithRetry(() => import("./pages/AcceptableUsePage.tsx"), "AcceptableUsePage");
const RefundPage = lazyWithRetry(() => import("./pages/RefundPage.tsx"), "RefundPage");
const AboutPage = lazyWithRetry(() => import("./pages/AboutPage.tsx"), "AboutPage");
const HowItWorksPage = lazyWithRetry(() => import("./pages/HowItWorksPage.tsx"), "HowItWorksPage");
const MessagesPage = lazyWithRetry(() => import("./pages/MessagesPage.tsx"), "MessagesPage");
const AssistantPage = lazyWithRetry(() => import("./pages/AssistantPage.tsx"), "AssistantPage");
const FoundingCreatorsPage = lazyWithRetry(() => import("./pages/FoundingCreatorsPage.tsx"), "FoundingCreatorsPage");
const TrustPage = lazyWithRetry(() => import("./pages/TrustPage.tsx"), "TrustPage");
const ContentPolicyPage = lazyWithRetry(() => import("./pages/ContentPolicyPage.tsx"), "ContentPolicyPage");
const SupportPage = lazyWithRetry(() => import("./pages/SupportPage.tsx"), "SupportPage");
const CalculatorPage = lazyWithRetry(() => import("./pages/CalculatorPage.tsx"), "CalculatorPage");
const TicketsPage = lazyWithRetry(() => import("./pages/TicketsPage.tsx"), "TicketsPage");
const NotFound = lazyWithRetry(() => import("./pages/NotFound.tsx"), "NotFound");
const ResetPasswordPage = lazyWithRetry(() => import("./pages/ResetPasswordPage.tsx"), "ResetPasswordPage");
const CheckoutReturnPage = lazyWithRetry(() => import("./pages/CheckoutReturnPage.tsx"), "CheckoutReturnPage");
const CompareOnlyFansPage = lazyWithRetry(() => import("./pages/CompareOnlyFansPage.tsx"), "CompareOnlyFansPage");
const OAuthConsent = lazyWithRetry(() => import("./pages/OAuthConsent.tsx"), "OAuthConsent");
const ExplorePage = lazyWithRetry(() => import("./pages/ExplorePage.tsx"), "ExplorePage");

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <PWAInstallPrompt />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<div className="min-h-screen bg-background text-foreground flex items-center justify-center">Loading...</div>}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/feed" element={<FeedPage />} />
              <Route path="/explore" element={<ExplorePage />} />

              <Route path="/creator/:username" element={<CreatorProfilePage />} />
              <Route path="/profile/:id" element={<CreatorProfilePage />} />
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route path="/create" element={<CreateRedirect />} />
              <Route path="/messages" element={<MessagesPage />} />
              <Route path="/assistant" element={<AssistantPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/admin/ceo-overview" element={<CEOOverviewPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/creator-agreement" element={<CreatorAgreementPage />} />
              <Route path="/age-disclaimer" element={<AgeDisclaimerPage />} />
              <Route path="/dmca" element={<DmcaPage />} />
              <Route path="/acceptable-use" element={<AcceptableUsePage />} />
              <Route path="/refund" element={<RefundPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/founding-creators" element={<FoundingCreatorsPage />} />
              <Route path="/trust" element={<TrustPage />} />
              <Route path="/content-policy" element={<ContentPolicyPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/calculator" element={<CalculatorPage />} />
              <Route path="/tickets" element={<TicketsPage />} />
              {/* Public: password recovery landing (Supabase resetPasswordForEmail redirect target). */}
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              {/* Public: Stripe Embedded Checkout return_url target. Verifies via webhook. */}
              <Route path="/checkout/return" element={<CheckoutReturnPage />} />
              <Route path="/compare" element={<CompareOnlyFansPage />} />
              <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
              <Route path="*" element={<NotFound />} />

            </Routes>
          </Suspense>
          <SafeSupportChatWidget />

        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
